import { useAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Card, IconSvg, Text, Tooltip } from '@/lib/v2/components';
import { InfoIcon, OpenNewWindow } from '@/lib/v2/icons/outline';
import { Table } from '@/lib/v3/components';

import { TRANSLATE_INSIGHTS_ACTIONS_SUMMARY } from '@/src/modules/InsightsModule/constants';
import { LAST_ACTION_QUANTITY } from '@/src/presentation/components/LastActionsReport/constants';

import { useActionsSummaryColumns } from './hooks/useActionsSummaryColumns';

import { atomActionsList } from '@/modules/CampaignsModule/atoms/actionsAtom';
import { ChannelInfoByID } from '@/modules/CampaignsModule/components/CampaignActions/ActionsTable/hooks/useActionsTableColumns';
import { CAMPAIGNS_PATH, WHATSAPP_PREFIX } from '@/modules/CampaignsModule/constants';
import { useCampaignActions } from '@/modules/CampaignsModule/hooks/useCampaignActions';
import {
  EActionTypes,
  ICampaignActionsListPayload,
  ITableCampaignActions,
} from '@/modules/CampaignsModule/interfaces/CampaignActions';

const payload: ICampaignActionsListPayload = {
  page: 1,
  limit: LAST_ACTION_QUANTITY,
  sortField: 'exeDate',
  sortOrder: 'desc',
  type: EActionTypes.FINISHED,
};

const ActionsSummary = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['insights']);

  const [actionsList, setActionsList] = useAtom(atomActionsList);

  const { getList, loading } = useCampaignActions();
  const { columns } = useActionsSummaryColumns();

  useEffect(() => {
    void (async () => await getList(payload))();

    return () => {
      setActionsList(RESET);
    };
  }, [getList, setActionsList]);

  const handleRowClick = useCallback(
    (row: ITableCampaignActions) => {
      const { channel } = row.action;
      let { route } = ChannelInfoByID[channel as keyof typeof ChannelInfoByID];

      if (row.action.name.startsWith(WHATSAPP_PREFIX)) {
        route = 'whatsapp';
      }

      navigate(
        `${CAMPAIGNS_PATH}/${row.action.campaignId}/${route}/${row.action.actionId}/summary`
      );
    },
    [navigate]
  );

  return (
    <Card
      noShadow
      className="flex w-full justify-between pb-0"
      id="insights-actions"
      isFullHeight={false}
    >
      <div className="mb-4 mt-1 flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <Text className="pl-4" color="black" fontWeight="normal" variant="headline">
            {t(`${TRANSLATE_INSIGHTS_ACTIONS_SUMMARY}.title`)}
          </Text>
          <div className="-mt-2">
            <Tooltip
              isDark
              content={
                <Text className="w-40 text-white" variant="text-sm">
                  {t(`${TRANSLATE_INSIGHTS_ACTIONS_SUMMARY}.tooltip`)}
                </Text>
              }
              position="top"
            >
              <IconSvg
                height="24px"
                strokeColor="secondary"
                svgComponent={<InfoIcon />}
                width="24px"
              />
            </Tooltip>
          </div>
        </div>

        <Link
          className="flex flex-row items-center gap-2 text-12 text-emblue-primary"
          data-dd-action-name={`insights.goToCampaigns`}
          id="insights-actions-goToCampaigns"
          to={'/v2/campaigns'}
        >
          {t(`${TRANSLATE_INSIGHTS_ACTIONS_SUMMARY}.goToCampaigns`)}
          <IconSvg svgComponent={<OpenNewWindow />} />
        </Link>
      </div>

      <Table
        columns={columns}
        data={actionsList || []}
        emptyState={{
          hasFiltersApplied: false,
          noDataResultComponent: (
            <div className="py-14">
              <Text alignment="center" color="gray" variant="text">
                {t(`${TRANSLATE_INSIGHTS_ACTIONS_SUMMARY}.emptyActions`)}
              </Text>
            </div>
          ),
          noFilterResultComponent: '',
        }}
        isLoading={loading}
        onRowClick={handleRowClick}
      />
    </Card>
  );
};

export default memo(ActionsSummary);
